import dayjs from 'dayjs';
import { STATUS } from '../../constants/EmiSchedule.component';
import { Due } from '../../interfaces/CaseDetail.type';
import { EmiFeeActions, FeeWaiverHistory, GroupedWaiverHistory } from './interfaces';

export const groupByMonthAndYear = (feeWaiverHistory: FeeWaiverHistory[]): GroupedWaiverHistory => {
  return feeWaiverHistory.reduce((acc, feeWaiverItem) => {
    const date = new Date(feeWaiverItem.createdAt);
    const monthYearKey = date.toLocaleString('default', { month: 'short', year: 'numeric' });
    if (!acc[monthYearKey]) {
      acc[monthYearKey] = [];
    }
    acc[monthYearKey].push(feeWaiverItem);
    return acc;
  }, {} as GroupedWaiverHistory);
};

const getEmiFeeAction = (emiItem: Due, unpaidDays: number): EmiFeeActions | null => {
  const { additionalWaiverInfo, unpaidDueAmount, totalPenalty, status, paidAmount } = emiItem || {};
  const { maxWaivableAmount, isWaivable, maxRefundableAmount } = additionalWaiverInfo || {};
  if (!totalPenalty) {
    return null;
  }
  if (isWaivable) {
    if ((unpaidDays > 30 && paidAmount > 0) || (unpaidDays <= 30 && status === STATUS.PAID)) {
      return EmiFeeActions.WAIVE;
    }
  }

  if (maxRefundableAmount > 0) {
    return EmiFeeActions.ADJUST;
  }
  const canAmountBeAdjusted = maxWaivableAmount >= unpaidDueAmount;
  if (status === STATUS.UNPAID) {
    if (canAmountBeAdjusted) {
      return EmiFeeActions.ADJUST;
    } else {
      return EmiFeeActions.HOLD;
    }
  }
  return null;
};

export const feeWaiveTransformedEmiData = (emiItem: Due, unpaidDays: number): Due => {
  const { additionalWaiverInfo, holdAmount, waivedAmount } = emiItem || {};
  const {
    isWaivable,
    maxAmountAlreadyWaived,
    refundedAmount,
    maxWaivableAmount,
    maxRefundableAmount
  } = additionalWaiverInfo || {};
  const waiverOrHoldAmount = holdAmount || waivedAmount || 0;
  const action = getEmiFeeAction(emiItem, unpaidDays);
  const disableHoldBtn = getDisableHoldBtn(unpaidDays, maxAmountAlreadyWaived, isWaivable, action);
  const showHoldTooltip = getShowToolTip(unpaidDays, maxAmountAlreadyWaived, isWaivable, action);
  const isColendingFlow = refundedAmount > 0 || maxRefundableAmount > 0;
  return {
    ...emiItem,
    waiverOrHoldAmount,
    isMaxWaived: getIsMaxWaived(unpaidDays, isWaivable, action),
    action,
    showHoldTooltip,
    disableHoldBtn,
    isColendingFlow,
    unpaidDays,
    additionalWaiverInfo: {
      ...emiItem.additionalWaiverInfo,
      maxWaivableAmount: (maxRefundableAmount || 0) + maxWaivableAmount
    }
  };
};

const getIsMaxWaived = (
  unpaidDays: number,
  isWaivable: boolean,
  action: EmiFeeActions
): boolean => {
  if (unpaidDays > 30) {
    return !action;
  }
  return !isWaivable;
};

const getDisableHoldBtn = (
  unpaidDays: number,
  maxAmountAlreadyWaived: boolean,
  isWaivable: boolean,
  action: EmiFeeActions | null
): boolean => {
  if (unpaidDays > 30) {
    return !action;
  }
  return Boolean(!action || (!isWaivable && !maxAmountAlreadyWaived));
};

const getShowToolTip = (
  unpaidDays: number,
  maxAmountAlreadyWaived: boolean,
  isWaivable: boolean,
  action: EmiFeeActions | null
): boolean => {
  if (unpaidDays > 30) {
    return !action;
  }
  return Boolean(action && !isWaivable && !maxAmountAlreadyWaived);
};

export const getFeeWaiverButtonLabel = (
  action: EmiFeeActions | null = EmiFeeActions.HOLD,
  waivedValue: number,
  unpaidDueAmount: number,
  holdAllowedTill: string
) => {
  const holdAllowedTillDate = holdAllowedTill ? dayjs(holdAllowedTill).format('DD-MM-YYYY') : '';
  let buttonTitle =
    action === EmiFeeActions.WAIVE ? 'Waive Fee' : `Hold fee (till ${holdAllowedTillDate} 11:59pm)`;
  if (action === EmiFeeActions.ADJUST) {
    buttonTitle = 'Waive / Hold';
    if (Number(waivedValue) && Number(waivedValue) < unpaidDueAmount) {
      buttonTitle = `Hold fee (till ${holdAllowedTillDate} 11:59pm)`;
    }
    if (Number(waivedValue) >= unpaidDueAmount) {
      buttonTitle = 'Waive fee';
    }
  }
  return buttonTitle;
};
